import React from 'react';
// Import individual module components from the 'modules' subfolder
import IntroductionModule from './modules/IntroductionModule';
import AlternatePortraitsModule from './modules/AlternatePortraitsModule';
import IntroductionClassModule from './modules/IntroductionClassModule';
import ExpansionFeaturesModule from './modules/ExpansionFeaturesModule';
import FaqModule from './modules/FaqModule';
import ClassFeaturesModule from './modules/ClassFeaturesModule';
import ClassDeckRecipesModule from './modules/ClassDeckRecipesModule';
import LoreSlidesModule from './modules/LoreSlidesModule';
import MainCardsShowcaseModule from './modules/MainCardsShowcaseModule';
import PackSimulatorModule from './modules/PackSimulatorModule';
import IntroWithVideo from './modules/IntroWithVideo';
import TextMediaModule from './modules/TextMediaModule';
import FeatureGridModule from './modules/FeatureGridModule';
import SoloAdventureModule from './modules/SoloAdventureModule';

// Import the icon for the quick edit button
import { FaPencilAlt } from 'react-icons/fa';

// Helper to check if a module is considered "empty"
const isModuleEmpty = (module) => {
     if (!module) return true;

    const hasAttributes = module.data && Object.values(module.data).some(value => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== '' && value !== null && value !== undefined;
    });

    const hasFeatures = module.features && module.features.length > 0;

    if (module.type === 'main-cards-showcase') return !(module.data?.cards && module.data.cards.length > 0);
    if (module.type === 'alternate-portraits') return !(module.data?.title || module.data?.content || (module.data?.cards && module.data.cards.length > 0));
    if (['expansion-features', 'faq', 'class-features', 'class-deck-recipes', 'lore-slides', 'solo-adventure'].includes(module.type)) return !hasFeatures && !(module.data?.title || module.data?.intro || module.data?.keyArt);
    return !hasAttributes && !hasFeatures; // Default check
};


function ModuleRenderer({ modules = [], currentSetId, isOwner, onEditModule, availableModules = {}, onImageClick }) {

    if (!modules || modules.length === 0) {
        return <p className="text-center p-5">Add modules using the editor panel to build your set page.</p>;
    }

    return (
        <div>
            {modules.map((module, index) => {
                const isFirstModule = index === 0;
                const isEmpty = isModuleEmpty(module);
                let ModuleComponent;

                switch (module.type) {
                    case 'introduction': ModuleComponent = IntroductionModule; break;
                    case 'alternate-portraits': ModuleComponent = AlternatePortraitsModule; break;
                    case 'introduction-class': ModuleComponent = IntroductionClassModule; break;
                    case 'expansion-features': ModuleComponent = ExpansionFeaturesModule; break;
                    case 'video-introduction': ModuleComponent = IntroWithVideo; break;
                    case 'faq': ModuleComponent = FaqModule; break;
                    case 'class-features': ModuleComponent = ClassFeaturesModule; break;
                    case 'class-deck-recipes': ModuleComponent = ClassDeckRecipesModule; break;
                    case 'lore-slides': ModuleComponent = LoreSlidesModule; break;
                    case 'main-cards-showcase': ModuleComponent = MainCardsShowcaseModule; break;
                    case 'pack-simulator': ModuleComponent = PackSimulatorModule; break;
                    case 'text-media': ModuleComponent = TextMediaModule; break;
                    case 'feature-grid': ModuleComponent = FeatureGridModule; break;
                    case 'solo-adventure': ModuleComponent = SoloAdventureModule; break;
                    default: ModuleComponent = ({ module }) => <div className="container p-3 my-3 border bg-warning">Unknown module type: {module.type}</div>;
                }

                const dividerStyle = { backgroundImage: "url('https://www.hearthcards.net/newsets/images/divider.png')" };
                const moduleWrapperStyle = { position: 'relative' };
                const moduleName = availableModules[module.type]?.name || 'Module';

                return (
                    <div key={module.id || index} style={moduleWrapperStyle} className={`set-module-wrapper set-module-type-${module.type}`}>
                        {!isFirstModule && <div className="set-module-divider" style={dividerStyle}></div>}

                        {/* Quick Edit Button (Conditional based on Ownership) */}
                        {isOwner && onEditModule && (
                            <button
                                className="set-module-quick-edit btn btn-sm btn-outline-secondary"
                                style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, lineHeight: 1, padding: '0.25rem 0.5rem' }}
                                onClick={() => onEditModule(index)}
                                title={`Edit ${moduleName}`}
                            >
                                <FaPencilAlt />
                            </button>
                        )}

                        {/* Empty Placeholder or Module Content */}
                        {isEmpty ? (
                            <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', minHeight: '200px', border: '1px dashed #ccc', margin: '1rem 0', position: 'relative' }}>
                                <p className="text-muted m-0">{moduleName} is empty</p>
                                {/* Optional: Add edit button to empty state too */}
                                {/* <button style={{ position: 'absolute', top: '10px', right: '10px' }} onClick={() => onEditModule(index)}>Edit</button> */}
                            </div>
                        ) : (
                             <ModuleComponent module={module} currentSetId={currentSetId} onImageClick={onImageClick} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default ModuleRenderer;
